// src/components/ProductManagementTable.tsx
import { useState } from 'react';
import { Link } from 'react-router-dom';
import productService, { Product } from '../services/productService';

interface ProductManagementTableProps {
  products: Product[];
  onProductUpdated: () => void;
  onProductDeleted: (id: number) => void;
}

type SortField = 'name' | 'price' | 'stock' | 'sold' | 'created_at';
type SortDirection = 'asc' | 'desc';

const ProductManagementTable: React.FC<ProductManagementTableProps> = ({
  products,
  onProductUpdated,
  onProductDeleted
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [stockFilter, setStockFilter] = useState<'all' | 'in_stock' | 'low_stock' | 'out_of_stock'>('all');
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
  const [bulkAction, setBulkAction] = useState<'none' | 'delete' | 'update_stock'>('none');
  const [bulkStockValue, setBulkStockValue] = useState(0);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Filtrer et trier les produits
  const filteredAndSortedProducts = products
    .filter(product => {
      // Filtre par nom
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           product.description.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Filtre par stock
      let matchesStock = true;
      if (stockFilter === 'in_stock') matchesStock = product.stock > 5;
      else if (stockFilter === 'low_stock') matchesStock = product.stock > 0 && product.stock <= 5;
      else if (stockFilter === 'out_of_stock') matchesStock = product.stock === 0;
      
      return matchesSearch && matchesStock;
    })
    .sort((a, b) => {
      let aValue: any;
      let bValue: any;
      
      switch (sortField) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'price':
          aValue = a.price;
          bValue = b.price;
          break;
        case 'stock':
          aValue = a.stock;
          bValue = b.stock;
          break;
        case 'sold':
          aValue = a.sold || 0;
          bValue = b.sold || 0;
          break;
        case 'created_at':
          aValue = new Date(a.created_at!);
          bValue = new Date(b.created_at!);
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedProducts(filteredAndSortedProducts.map(p => p.id));
    } else {
      setSelectedProducts([]);
    }
  };

  const handleSelectProduct = (productId: number, checked: boolean) => {
    if (checked) {
      setSelectedProducts([...selectedProducts, productId]);
    } else {
      setSelectedProducts(selectedProducts.filter(id => id !== productId));
    }
  };

  const handleBulkAction = async () => {
    if (selectedProducts.length === 0) return;

    try {
      if (bulkAction === 'delete') {
        if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedProducts.length} produit(s) ?`)) {
          for (const productId of selectedProducts) {
            await productService.deleteProduct(productId);
            onProductDeleted(productId);
          }
          setSelectedProducts([]);
        }
      } else if (bulkAction === 'update_stock') {
        if (window.confirm(`Mettre à jour le stock de ${selectedProducts.length} produit(s) à ${bulkStockValue} unités ?`)) {
          for (const productId of selectedProducts) {
            await productService.updateProduct(productId, { stock: bulkStockValue });
          }
          onProductUpdated();
          setSelectedProducts([]);
        }
      }
    } catch (error) {
      console.error('Erreur lors de l\'action groupée:', error);
      alert('Erreur lors de l\'action groupée');
    }
    
    setShowBulkActions(false);
    setBulkAction('none');
  };

  const duplicateProduct = async (product: Product) => {
    try {
      const duplicatedData = {
        name: `${product.name} (copie)`,
        description: product.description,
        price: product.price,
        category_id: product.category_id,
        stock: 0
      };
      
      // Note: Pour la duplication, on ne peut pas copier l'image directement
      // Il faudrait télécharger l'image existante et la re-uploader
      alert('Fonctionnalité de duplication en cours de développement');
    } catch (error) {
      console.error('Erreur lors de la duplication:', error);
      alert('Erreur lors de la duplication');
    }
  };

  const getStockStatus = (stock: number) => {
    if (stock === 0) return { text: 'Épuisé', color: 'text-red-600 bg-red-100' };
    if (stock <= 5) return { text: 'Stock faible', color: 'text-yellow-600 bg-yellow-100' };
    return { text: 'En stock', color: 'text-green-600 bg-green-100' };
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 text-olive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-olive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 13l-5 5m0 0l-5-5m5 5V6" />
      </svg>
    );
  };

  return (
    <div className="space-y-4">
      {/* Filtres et recherche */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          {/* Recherche */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Rechercher un produit..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-olive w-full"
              />
            </div>
          </div>

          {/* Filtres */}
          <div className="flex gap-4 items-center">
            <select
              value={stockFilter}
              onChange={(e) => setStockFilter(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-olive"
            >
              <option value="all">Tous les stocks</option>
              <option value="in_stock">En stock</option>
              <option value="low_stock">Stock faible</option>
              <option value="out_of_stock">Épuisé</option>
            </select>

            {/* Actions groupées */}
            {selectedProducts.length > 0 && (
              <button
                onClick={() => setShowBulkActions(!showBulkActions)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
              >
                Actions ({selectedProducts.length})
              </button>
            )}
          </div>
        </div>

        {/* Panel d'actions groupées */}
        {showBulkActions && selectedProducts.length > 0 && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
            <div className="flex items-center gap-4">
              <select
                value={bulkAction}
                onChange={(e) => setBulkAction(e.target.value as any)}
                className="px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="none">Choisir une action</option>
                <option value="delete">Supprimer sélectionnés</option>
                <option value="update_stock">Mettre à jour le stock</option>
              </select>

              {bulkAction === 'update_stock' && (
                <input
                  type="number"
                  placeholder="Nouveau stock"
                  value={bulkStockValue}
                  onChange={(e) => setBulkStockValue(parseInt(e.target.value) || 0)}
                  className="px-3 py-2 border border-gray-300 rounded-md w-32"
                  min="0"
                />
              )}

              <button
                onClick={handleBulkAction}
                disabled={bulkAction === 'none'}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition disabled:opacity-50"
              >
                Exécuter
              </button>

              <button
                onClick={() => setShowBulkActions(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition"
              >
                Annuler
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Statistiques rapides */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-md p-4 text-center">
          <p className="text-2xl font-bold text-slate">{filteredAndSortedProducts.length}</p>
          <p className="text-sm text-gray-600">Produits affichés</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4 text-center">
          <p className="text-2xl font-bold text-green-600">
            {filteredAndSortedProducts.filter(p => p.stock > 5).length}
          </p>
          <p className="text-sm text-gray-600">En stock</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4 text-center">
          <p className="text-2xl font-bold text-yellow-600">
            {filteredAndSortedProducts.filter(p => p.stock > 0 && p.stock <= 5).length}
          </p>
          <p className="text-sm text-gray-600">Stock faible</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4 text-center">
          <p className="text-2xl font-bold text-red-600">
            {filteredAndSortedProducts.filter(p => p.stock === 0).length}
          </p>
          <p className="text-sm text-gray-600">Épuisés</p>
        </div>
      </div>

      {/* Tableau des produits */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedProducts.length === filteredAndSortedProducts.length && filteredAndSortedProducts.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="rounded border-gray-300 text-olive focus:ring-olive"
                  />
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Produit</span>
                    {getSortIcon('name')}
                  </div>
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('price')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Prix</span>
                    {getSortIcon('price')}
                  </div>
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('stock')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Stock</span>
                    {getSortIcon('stock')}
                  </div>
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('sold')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Vendus</span>
                    {getSortIcon('sold')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAndSortedProducts.map((product) => {
                const stockStatus = getStockStatus(product.stock);
                const isSelected = selectedProducts.includes(product.id);
                
                return (
                  <tr key={product.id} className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => handleSelectProduct(product.id, e.target.checked)}
                        className="rounded border-gray-300 text-olive focus:ring-olive"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-12 w-12">
                          <img 
                            className="h-12 w-12 rounded-lg object-cover border"
                            src={productService.getImageUrl(product.image_url)} 
                            alt={product.name}
                            onError={(e) => {
                              e.currentTarget.src = '/placeholder-image.jpg';
                            }}
                          />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{product.name}</div>
                          <div className="text-sm text-gray-500 max-w-xs truncate">
                            {product.description}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">{product.price.toLocaleString()} FCFA</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{product.stock} unités</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{product.sold || 0} unités</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${stockStatus.color}`}>
                        {stockStatus.text}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <Link 
                          to={`/edit-product/${product.id}`}
                          className="text-olive hover:text-terracotta transition"
                          title="Modifier"
                        >
                          ✏️
                        </Link>
                        <button 
                          onClick={() => duplicateProduct(product)}
                          className="text-blue-600 hover:text-blue-900 transition"
                          title="Dupliquer"
                        >
                          📋
                        </button>
                        <button 
                          onClick={() => {
                            if (window.confirm(`Supprimer "${product.name}" ?`)) {
                              onProductDeleted(product.id);
                            }
                          }}
                          className="text-red-600 hover:text-red-900 transition"
                          title="Supprimer"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredAndSortedProducts.length === 0 && (
          <div className="p-12 text-center">
            <div className="text-gray-500 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0118 12M6 20.291A7.962 7.962 0 016 12m0 8.291A7.962 7.962 0 016 12m0 0V9a2 2 0 012-2h8a2 2 0 012 2v3m0 0V9a2 2 0 00-2-2H6a2 2 0 00-2 2v3m0 0a2 2 0 002 2h8a2 2 0 002-2" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun produit trouvé</h3>
            <p className="text-gray-600">
              {searchTerm || stockFilter !== 'all' 
                ? 'Essayez de modifier vos filtres de recherche'
                : 'Vous n\'avez pas encore de produits'
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductManagementTable;